import { CloseButton, Menu, Table } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
	IconChevronDown,
	IconChevronUp,
	IconInfoCircle,
	IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import {
	formatTillId,
	formatToCurrency,
	formatToDateString,
	formatToTimeString,
} from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { PosMessageDto } from '../types/PosMessageDto';
import { DeletePosMessageModal } from './DeletePosMessageModal';
import { EditPosMessageModal } from './EditPosMessageModal';
import { PosMessageView } from './PosMessageView';
import { KeyedRow } from '../../../components/KeyedRow';

export function PosMessageRow({
	initialPosMessage,
	updateList,
}: { initialPosMessage: PosMessageDto; updateList: () => void }) {
	const [posMessage, setPosMessage] = useState(initialPosMessage);
	const [messageOpen, setMessageOpen] = useState(false);

	const [showDeleteModal, { open: openDeleteModal, close: closeDeleteModal }] =
		useDisclosure(false);

	const [showEditModal, { open: openEditModal, close: closeEditModal }] =
		useDisclosure(false);

	return (
		<>
			<EditPosMessageModal
				close={closeEditModal}
				opened={showEditModal}
				updatePosMessageRecord={(posMessage: PosMessageDto) =>
					setPosMessage(posMessage)
				}
				posMessage={{
					branchId: posMessage.branchId,
					tillNo: formatTillId(
						posMessage.till,
						posMessage.tillId,
						posMessage.branchId,
					),
					id: posMessage.id,
					startDate: new Date(posMessage.startDate),
					endDate: new Date(posMessage.endDate),
					days: getDaysOfPosMessage(posMessage),
					posMessage: posMessage.posMessage,
					skuid: posMessage.skuid,
					moneyValue: posMessage.moneyValue.toString(),
					qty: posMessage.qty.toString(),
				}}
			/>
			<DeletePosMessageModal
				close={closeDeleteModal}
				opened={showDeleteModal}
				triggerListUpdate={() => updateList()}
				posMessageId={posMessage.id.toString()}
			/>
			<KeyedRow>
				<Table.Td>{formatToDateString(posMessage.startDate)}</Table.Td>
				<Table.Td>{formatToTimeString(posMessage.startDate)}</Table.Td>
				<Table.Td>{formatToDateString(posMessage.endDate)}</Table.Td>
				<Table.Td>{formatToTimeString(posMessage.endDate)}</Table.Td>
				<Table.Td>{undefinedCheck(posMessage.branch)}</Table.Td>
				<Table.Td>{undefinedCheck(posMessage.till)}</Table.Td>
				<Table.Td>{formatToCurrency(posMessage.moneyValue)}</Table.Td>
				<Table.Td>{posMessage.qty}</Table.Td>
				<Table.Td>{undefinedCheck(posMessage.skuid)}</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!messageOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setMessageOpen(!messageOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconInfoCircle {...defaultIconStyle} />}
							onClick={() => openEditModal()}
						>
							Edit POS Message
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => openDeleteModal()}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={messageOpen} columnRows={12}>
				<PosMessageView posMessage={posMessage} />
			</TableRowCollapsible>
		</>
	);
}

export function getDaysOfPosMessage(message: PosMessageDto) {
	const days: string[] = [];

	if (message.mon) days.push('Monday');
	if (message.tue) days.push('Tuesday');
	if (message.wed) days.push('Wednesday');
	if (message.thur) days.push('Thursday');
	if (message.fri) days.push('Friday');
	if (message.sat) days.push('Saturday');
	if (message.sun) days.push('Sunday');

	return days;
}
